/**
 * apps/web/src/lib/strings/en.ts
 *
 * English UI strings. Must mirror the exact shape of `./it.ts`; the `: Strings`
 * annotation makes a missing OR extra key a compile error, so this file can
 * never silently fall out of sync with the source dictionary.
 *
 * NOTE: per house style we never use the em-dash character; use a hyphen,
 * comma, or parentheses instead.
 */

import type { Strings } from './it';

export const en: Strings = {
  app: {
    name: 'glarno',
    tagline: 'The AI operating system for your business',
  },
  nav: {
    oggi: 'Today',
    clienti: 'Clients',
    progetti: 'Projects',
    soldi: 'Money',
    documenti: 'Documents',
    messaggi: 'Messages',
    team: 'Team',
    report: 'Reports',
    libreria: 'Library',
    tabella: 'Table',
    governance: 'Governance',
    automazioni: 'Automations',
    impostazioni: 'Settings',
    abbonamento: 'Subscription',
    altro: 'More',
    logout: 'Sign out',
    ariaPrimary: 'Main navigation',
    ariaQuick: 'Quick navigation',
    ariaAll: 'All sections',
  },
  common: {
    loading: 'Loading...',
    retry: 'Try again',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    search: 'Search',
    empty: 'Nothing to show',
    errorTitle: 'Something went wrong',
    errorGeneric: 'An error occurred. Please try again shortly.',
    reload: 'Reload',
    updateAvailableTitle: 'Update available',
    updateAvailableBody: 'A new version of glarno is available. Reload to continue.',
    comingSoon: 'Coming soon',
    comingSoonBody: 'This section is under construction and will be available soon.',
  },
  auth: {
    loginTitle: 'Welcome back',
    loginSubtitle: 'Sign in to your glarno workspace',
    emailLabel: 'Email',
    emailPlaceholder: 'name@company.com',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Your password',
    submit: 'Sign in',
    submitting: 'Signing in...',
    invalidCredentials: 'Incorrect email or password',
    sessionExpired: 'Your session expired, please sign in again',
  },
  oggi: {
    title: 'Today',
    subtitle: 'Your operations hub: approvals, urgencies, AI in progress.',
    summaryFallback: 'Here are the most important actions for today.',
    summaryLabel: 'Daily summary',
    generatedAt: 'Updated',
    refresh: 'Refresh',
    sectionApprovals: 'Actions to approve',
    sectionApprovalsDesc: 'AI actions ready: approve and send, edit, or dismiss.',
    sectionUrgencies: 'Urgencies',
    sectionUrgenciesDesc: 'Overdue invoices, silent clients, blocked tasks.',
    sectionDecisions: 'To decide',
    sectionDecisionsDesc: 'Reconciliations, classifications, and follow-ups that need a choice.',
    sectionAiInProgress: 'AI work in progress',
    sectionAiInProgressDesc: 'Running agent activity, with status and the option to stop.',
    quickCommandsTitle: 'Quick commands',
    emptyFeedTitle: 'All under control',
    emptyFeedDesc: 'There are no pending actions. New AI proposals will appear here.',
    emptySectionApprovals: 'No actions to approve.',
    emptySectionUrgencies: 'No urgencies right now.',
    emptySectionDecisions: 'Nothing to decide right now.',
    emptySectionAiInProgress: 'No AI work in progress.',
    mockNotice: 'Demo data: the Today source is not active yet.',
    talkSubmit: 'Send',
    talkLabel: 'What would you like to do?',
    talkPlaceholder: 'E.g. Prepare a quote for client Rossi',
    noteSubmit: 'Record',
    noteLabel: 'Note',
    notePlaceholder: 'Write the note here...',
    draftPreviewLabel: 'Proposal preview',
    draftPreviewPlaceholder: 'AI proposal text...',
  },
  card: {
    why: 'Why',
    contextUsed: 'Context used',
    sourceOpenExternal: 'Open in source',
    proposedActions: 'Proposed actions',
    confidence: 'Confidence',
    risk: 'Risk',
    riskLow: 'low',
    riskMedium: 'medium',
    riskHigh: 'high',
    categoryNeedsApproval: 'To approve',
    categoryUrgency: 'Urgency',
    categoryNeedsDecision: 'To decide',
    categoryAiInProgress: 'AI in progress',
    approveAndSend: 'Approve and send',
    edit: 'Edit',
    onlyCreateTask: 'Just create task',
    ignore: 'Dismiss',
    openDetails: 'View details',
    cancel: 'Cancel',
    started: 'Started',
    estimated: 'Estimate',
    approving: 'Sending...',
    approved: 'Approved and sent',
    rejected: 'Dismissed',
    taskCreated: 'Task created',
    actionFailed: 'Action failed. Try again.',
    editNotAvailable: 'Inline editing is coming soon. For now you can approve or just create the task.',
    confirmIgnoreTitle: 'Dismiss this action?',
    confirmIgnoreDesc: 'The AI proposal will be discarded and will not run.',
    expiresIn: 'Expires in',
  },
  quickCommands: {
    talkToAi: 'Talk to AI',
    createProject: 'Create project',
    recordNote: 'Record note',
    uploadDocument: 'Upload document',
    createQuote: 'Create quote',
    writeEmail: 'Write email',
    createTask: 'Create task',
    soon: 'Coming soon',
  },
  soldi: {
    tabInvoices: 'Invoices',
    tabMovements: 'Transactions',
    tabExpenses: 'Expenses',
    summaryPaid: 'Collected',
    summaryUnpaid: 'To collect',
    summaryOverdue: 'Overdue',
    salesInvoices: 'Sales invoices',
    purchaseInvoices: 'Purchase invoices',
    invoiceNumber: 'Number',
    company: 'Client',
    amount: 'Amount',
    dueDate: 'Due date',
    status: 'Status',
    noInvoices: 'No invoices',
    noInvoicesDesc: 'Sales and purchase invoices appear here.',
    unmatchedMovements: 'Transactions to reconcile',
    unmatchedDesc: 'Bank transactions without a linked document.',
    reconciled: 'Reconciled',
    counterparty: 'Counterparty',
    valueDate: 'Value date',
    proposeMatch: 'Propose match',
    proposing: 'Analyzing...',
    suggestedMatch: 'Suggested match',
    noConfidentMatch: 'No confident match found. Upload or link a document.',
    confirmMatch: 'Confirm match',
    confirming: 'Confirming...',
    matchConfirmed: 'Transaction reconciled',
    matchInvoice: 'Invoice',
    matchExpense: 'Expense',
    confidence: 'Confidence',
    autoConfirm: 'Confident match',
    needsApproval: 'Needs confirmation',
    noMovements: 'No transactions',
    noMovementsDesc: 'When you connect your bank via PSD2, transactions appear here.',
    missingDocsTitle: 'Missing documents',
    missingDocsDesc: 'Transactions below the safety threshold that require a document.',
    pendingExpenses: 'Expenses to approve',
    expenseDescription: 'Description',
    category: 'Category',
    date: 'Date',
    approve: 'Approve',
    reject: 'Reject',
    markPaid: 'Mark paid',
    expenseApproved: 'Expense approved',
    expenseRejected: 'Expense rejected',
    expensePaid: 'Expense paid',
    noExpenses: 'No expenses',
    noExpensesDesc: 'Expenses submitted by the team appear here for approval.',
    actionFailed: 'Operation failed. Try again.',
  },
  invoiceStatus: {
    draft: 'Draft',
    sent: 'Sent',
    paid: 'Paid',
    overdue: 'Overdue',
    cancelled: 'Cancelled',
    void: 'Cancelled',
  },
  expenseStatus: {
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    paid: 'Paid',
  },
  documenti: {
    filterAll: 'All',
    name: 'Name',
    classification: 'Classification',
    size: 'Size',
    uploaded: 'Uploaded',
    noDocuments: 'No documents',
    noDocumentsDesc: 'Documents uploaded and classified by AI appear here.',
    noResults: 'No documents for this filter',
    noResultsDesc: 'Try changing the selected classification.',
    unclassified: 'Unclassified',
  },
  documentClass: {
    contract: 'Contract',
    invoice: 'Invoice',
    receipt: 'Receipt',
    report: 'Report',
    misc: 'Other',
  },
  messaggi: {
    threads: 'Conversations',
    unreadOnly: 'Unread only',
    all: 'All',
    selectThread: 'Select a conversation',
    selectThreadDesc: 'Pick a thread on the left to read and reply.',
    noThreads: 'No conversations',
    noThreadsDesc: 'Email, WhatsApp, and SMS from your clients appear here.',
    aiDraft: 'AI draft',
    aiDraftReady: 'Draft ready',
    aiDraftDesc: 'glarno prepared a reply using the client context.',
    generateDraft: 'Generate AI draft',
    reviewDraft: 'Review draft',
    send: 'Send',
    reply: 'Reply',
    replyPlaceholder: 'Write a reply...',
    back: 'Conversations',
    channelEmail: 'Email',
    channelWhatsapp: 'WhatsApp',
    channelSms: 'SMS',
    channelCall: 'Call',
    channelInternal: 'Internal note',
    notLiveTitle: 'Inbox coming soon',
    notLiveDesc:
      'The messages source (/v1/conversations) is not active yet. The screen will connect automatically once the endpoint is available.',
  },
  sections: {
    clienti: { title: 'Clients', subtitle: 'Companies and contacts in your workspace.' },
    progetti: { title: 'Projects', subtitle: 'Projects, tasks, quotes, and invoices.' },
    soldi: { title: 'Money', subtitle: 'Invoices, expenses, bank transactions, reconciliations.' },
    documenti: { title: 'Documents', subtitle: 'Document archive and AI classification.' },
    messaggi: { title: 'Messages', subtitle: 'Multichannel business inbox with AI drafts.' },
    team: { title: 'Team', subtitle: 'Users, roles, and assignments.' },
    report: { title: 'Reports', subtitle: 'Operational KPIs and AI-generated insights.' },
    libreria: { title: 'Library', subtitle: 'AI-authored deliverables, with their cited sources.' },
    tabella: {
      title: 'AI Table',
      subtitle: 'A grid of clients with AI-computed columns and their cited sources.',
    },
    automazioni: { title: 'Automations', subtitle: 'Workflows, AI agents, and industry packs.' },
    governance: { title: 'Governance', subtitle: 'Activity log, security, and workspace compliance.' },
    impostazioni: { title: 'Settings', subtitle: 'Subscription, email intake, and workspace preferences.' },
    abbonamento: { title: 'Subscription', subtitle: 'Manage your plan and billing.' },
  },
  clienti: {
    searchPlaceholder: 'Search by company name...',
    newCompany: 'New client',
    newCompanyTitle: 'New client',
    countOne: '1 company',
    countMany: (n: number): string => `${n} companies`,
    emptyTitle: 'No clients',
    emptyDesc: 'When you add companies and contacts, they appear here.',
    emptySearchTitle: 'No results',
    emptySearchDesc: 'No company matches your search.',
    colName: 'Name',
    colType: 'Type',
    colEmail: 'Email',
    colPhone: 'Phone',
    colCountry: 'Country',
    backToList: 'Clients',
    tabTimeline: 'Timeline',
    tabContacts: 'Contacts',
    tabProjects: 'Projects',
    tabDocuments: 'Documents',
    tabInvoices: 'Invoices',
    identity: 'Details',
    fieldLegalName: 'Legal name',
    fieldVat: 'VAT number',
    fieldFiscalCode: 'Tax code',
    fieldEmail: 'Email',
    fieldPhone: 'Phone',
    fieldWebsite: 'Website',
    fieldCountry: 'Country',
    statProjects: 'Projects',
    statContacts: 'Contacts',
    statInvoices: 'Invoices',
    statDocuments: 'Documents',
    contactsEmpty: 'No contacts for this company.',
    projectsEmpty: 'No projects for this company.',
    documentsEmpty: 'No linked documents.',
    invoicesEmpty: 'No invoices for this company.',
    addContact: 'New contact',
    newContactTitle: 'New contact',
    openProject: 'Open project',
    formName: 'Company name',
    formNamePlaceholder: 'E.g. Rossi Construction Ltd',
    formLegalName: 'Legal name',
    formVat: 'VAT number',
    formEmail: 'Email',
    formPhone: 'Phone',
    formType: 'Type',
    createError: 'Could not create the client. Try again.',
    contactFirstName: 'First name',
    contactLastName: 'Last name',
    contactEmail: 'Email',
    contactPhone: 'Phone',
    contactRole: 'Role',
    contactRolePlaceholder: 'E.g. Head of purchasing',
    contactCreateError: 'Could not create the contact. Try again.',
  },
  companyType: {
    customer: 'Customer',
    supplier: 'Supplier',
    partner: 'Partner',
    prospect: 'Prospect',
  },
  progetti: {
    searchPlaceholder: 'Search by project name...',
    newProject: 'New project',
    newProjectTitle: 'New project',
    countOne: '1 project',
    countMany: (n: number): string => `${n} projects`,
    emptyTitle: 'No projects',
    emptyDesc: 'Projects you create, with tasks, quotes, and invoices, appear here.',
    emptySearchTitle: 'No results',
    emptySearchDesc: 'No project matches the filters.',
    filterAll: 'All',
    colName: 'Name',
    colStatus: 'Status',
    colBudget: 'Budget',
    colDates: 'Period',
    backToList: 'Projects',
    tabTasks: 'Tasks',
    tabQuote: 'Quote',
    tabDocuments: 'Documents',
    tabInvoices: 'Invoices',
    tabTimeline: 'Timeline',
    noClient: 'No client',
    budgetLabel: 'Budget',
    periodLabel: 'Period',
    marginLabel: 'Margin',
    revenueLabel: 'Revenue',
    costLabel: 'Costs',
    tasksEmpty: 'No tasks in this project.',
    addTask: 'New task',
    newTaskTitle: 'New task',
    quotesEmpty: 'No quote-contracts for this project.',
    addQuote: 'New quote',
    newQuoteTitle: 'New quote',
    documentsEmpty: 'No documents linked to this project.',
    invoicesEmpty: 'No invoices for this project.',
    formName: 'Project name',
    formNamePlaceholder: 'E.g. Client office renovation',
    formDescription: 'Description',
    formClient: 'Client',
    formClientNone: 'No client',
    formStatus: 'Status',
    formBudget: 'Budget',
    formBudgetPlaceholder: '0.00',
    createError: 'Could not create the project. Try again.',
    taskTitle: 'Title',
    taskTitlePlaceholder: 'E.g. Send updated quote',
    taskPriority: 'Priority',
    taskStatus: 'Status',
    taskCreateError: 'Could not create the task. Try again.',
    quoteTitleLabel: 'Quote title',
    quoteTitlePlaceholder: 'E.g. Supply proposal 2026',
    quoteCreateError: 'Could not create the quote. Try again.',
  },
  projectStatus: {
    active: 'Active',
    paused: 'Paused',
    completed: 'Completed',
    cancelled: 'Cancelled',
  },
  taskStatusLabel: {
    todo: 'To do',
    in_progress: 'In progress',
    needs_input: 'Awaiting input',
    blocked: 'Blocked',
    done: 'Done',
    cancelled: 'Cancelled',
  },
  taskPriorityLabel: {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    urgent: 'Urgent',
  },
  quoteStatusLabel: {
    draft: 'Draft',
    sent: 'Sent',
    signed: 'Signed',
    rejected: 'Rejected',
    expired: 'Expired',
  },
  timeline: {
    title: 'Timeline',
    empty: 'No activity recorded.',
    loadMore: 'Load more activity',
    loadingMore: 'Loading...',
  },
  start: {
    title: 'Get started with glarno',
    subtitle: 'Enter your email: we send you a link to sign in, no password needed.',
    emailLabel: 'Work email',
    emailPlaceholder: 'name@company.com',
    submit: 'Send me the link',
    submitting: 'Sending...',
    invalidEmail: 'Enter a valid email address.',
    haveAccount: 'Already have a password?',
    signInLink: 'Sign in',
    sentTitle: 'Check your inbox',
    sentBody: (email: string): string =>
      `If ${email} can sign in, you will find a link to enter in your inbox. It expires in 15 minutes.`,
    sentHint: 'Cannot find the email? Check your spam or request a new link.',
    resend: 'Send again',
    useAnother: 'Use another email',
    openEmailApp: 'Open email app',
  },
  magicLink: {
    verifying: 'Verifying the link...',
    successTitle: 'You are in',
    successBody: 'Signed in. We are preparing your workspace.',
    errorTitle: 'Invalid link',
    errorBody:
      'This sign-in link is invalid or has expired. Request a new one to continue.',
    requestNew: 'Request a new link',
    missingToken: 'The sign-in token is missing. Open the link from your email.',
  },
  onboarding: {
    skip: 'Skip',
    skipForNow: 'Skip for now',
    continue: 'Continue',
    back: 'Back',
    finish: 'Go to your workspace',
    optional: 'Optional',
    recommended: 'Recommended',
    quickWin: 'Quick win',
    minutes: (n: number): string => `${n} min`,
    ahaLabel: 'Your first milestone',
    ahaText: 'Connect your first tool and see your first action already waiting in your Today.',
    progress: (done: number, total: number): string => `${done} of ${total} completed`,
    segmentTitle: 'How do you work?',
    segmentSubtitle: 'We tailor glarno to how you work. You can change it later.',
    segmentFreelance: 'Freelancer / professional',
    segmentFreelanceDesc: 'I work on my own and handle everything myself.',
    segmentStudio: 'Studio / micro-business',
    segmentStudioDesc: 'We are a small team with recurring projects and clients.',
    segmentAgency: 'Agency / company',
    segmentAgencyDesc: 'We coordinate a team across multiple clients and projects.',
    segmentOther: 'Other',
    segmentOtherDesc: 'I want to explore and see what it can do for me.',
    checklistTitle: 'Your first steps',
    checklistSubtitle: 'Start with the quick win: the rest can wait.',
    stepDone: 'Done',
    stepStart: 'Start',
    stepConnect: 'Connect',
    stepReview: 'Open',
    stepSecure: 'Set up',
    connectTitle: 'Connect your tools',
    connectSubtitle:
      'Connect the tools you already use. glarno keeps your keys safe and encrypted.',
    yourTools: 'Your tools',
    weHandle: 'We handle it for you',
    weHandleSubtitle:
      'These services are included: glarno manages them for you, nothing to set up.',
    connectAction: 'Connect',
    connecting: 'Opening...',
    connected: 'Connected',
    notReady: 'Coming soon',
    notReadyHint: 'The connection keys are being activated. Check back soon.',
    included: 'Included',
    quickWinTitle: 'Here is your first action',
    quickWinSubtitle: 'glarno already found something useful for you.',
    quickWinSampleLabel: 'Today card preview',
    quickWinCardCta: 'You will see it in your Today',
    quickWinEmptyCta: 'Continue',
    quickWinContinue: 'Great, continue',
    quickWinNothing: 'Everything is in order here. Let us move on.',
    passwordTitle: 'Secure your account',
    passwordSubtitle:
      'Set a password to sign in whenever you want, even without a link. Last step.',
    passwordLabel: 'Choose a password',
    passwordPlaceholder: 'At least 8 characters',
    passwordConfirmLabel: 'Confirm password',
    passwordTooShort: 'The password must be at least 8 characters.',
    passwordMismatch: 'The two passwords do not match.',
    passwordSubmit: 'Set password and continue',
    passwordSaving: 'Saving...',
    passwordSkip: 'I will set the password later',
    passwordError: 'Could not save the password. Try again.',
    loadError: 'Could not load the tools. Try again.',
    authUrlError: 'Could not start the connection. Try again.',
  },
  impostazioni: {
    inboundTitle: 'Email forwarding with attachments',
    inboundDesc:
      'Forward emails with attachments here: we archive and index them automatically.',
    inboundCopy: 'Copy address',
    inboundCopied: 'Address copied',
    inboundCopyError: 'Copy failed. Select and copy it manually.',
    inboundLoadError: 'Could not load the forwarding address. Try again.',
    inboundAddressLabel: 'Forwarding address',
    languageTitle: 'Language',
    languageDesc: 'Choose the interface language. It applies to the whole workspace.',
    languageItalian: 'Italiano',
    languageEnglish: 'English',
    languageSaving: 'Saving...',
    languageSaved: 'Language updated',
    languageError: 'Could not update the language. Try again.',
    themeTitle: 'Appearance',
    themeDesc: 'Choose the light or dark theme, or follow your system setting.',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    billingTitle: 'Subscription and billing',
    billingDesc: 'Check your plan, change your subscription, or manage payments.',
    billingOpen: 'Open subscription',
    integrationsTitle: 'Integrations',
    integrationsDesc: 'Connect Gmail, Drive, Calendar and other tools. Manage the Claude connector.',
    integrationsOpen: 'Open integrations',
    preferencesTitle: 'AI preferences',
    preferencesDesc:
      'Standing rules the assistant honors in every reply: language, currency and tone.',
    preferencesLangLabel: 'Reply language',
    preferencesLangPlaceholder: 'e.g. English',
    preferencesCurrencyLabel: 'Currency',
    preferencesCurrencyPlaceholder: 'e.g. EUR',
    preferencesToneLabel: 'Tone',
    preferencesTonePlaceholder: 'e.g. formal',
    preferencesSave: 'Save',
    preferencesSaving: 'Saving...',
    preferencesSaved: 'Preference saved',
    preferencesError: 'Could not save the preference. Try again.',
    preferencesRemoveError: 'Could not remove the preference. Try again.',
    preferencesClear: 'Remove',
    preferencesInferredBadge: 'Suggested by AI',
    preferencesLoadError: 'Could not load preferences. Try again.',
  },
  libreria: {
    filterAll: 'All',
    filterBi: 'Analytics',
    filterDocument: 'Documents',
    filterSales: 'Sales',
    agentBi: 'Analytics',
    agentDocument: 'Document',
    agentSales: 'Sales',
    open: 'Open',
    close: 'Close',
    sourcesTitle: 'Sources',
    sourcesNone: 'No sources cited.',
    sourceOne: '1 source',
    sourceMany: (n: number): string => `${n} sources`,
    delete: 'Delete',
    deleteConfirm: 'Confirm deletion?',
    deleteError: 'Could not delete. Try again.',
    detailError: 'Could not open the document. Try again.',
    loadError: 'Could not load the library. Try again.',
    emptyTitle: 'The library is empty',
    emptyDescription:
      'AI-authored deliverables you have approved show up here, with their sources.',
  },
  // ── Tabella (AI table: clients x cited AI columns) ─────────────────────────
  tabella: {
    addColumn: 'Add column',
    runAll: 'Refresh all',
    stop: 'Stop',
    colClient: 'Client',
    run: 'Compute',
    retry: 'Retry',
    computing: 'Working...',
    cellEmpty: 'No data',
    runColumn: 'Compute column',
    removeColumn: 'Remove column',
    searchPlaceholder: 'Search client...',
    emptyTitle: 'No clients',
    emptyDesc: 'Add clients to build your AI table.',
    emptySearchTitle: 'No results',
    emptySearchDesc: 'No client matches your search.',
    formTitle: 'New AI column',
    formLabel: 'Column title',
    formLabelPlaceholder: 'E.g. Last contact',
    formPrompt: 'AI question',
    formPromptPlaceholder: 'What should the AI compute for each client?',
    formAgent: 'Agent',
    formSubmit: 'Add',
    agents: {
      inbox: 'Inbox',
      sales: 'Sales',
      project: 'Projects',
      finance: 'Finance',
      document: 'Documents',
      bi: 'Analytics',
    },
  },
  // ── Governance (audit trail + RBAC + EU compliance posture) ────────────────
  governance: {
    // Audit trail
    auditTitle: 'Activity log',
    auditSubtitle: 'An immutable trail of every change in the workspace.',
    colWhen: 'When',
    colActor: 'Actor',
    colAction: 'Action',
    colEntity: 'Object',
    systemActor: 'System',
    filterByEntity: 'Filter by this object',
    filterByAction: 'Filter by this action',
    activeFilters: 'Active filters',
    clearFilter: 'Remove filter',
    clearFilters: 'Clear filters',
    pageWord: 'Page',
    totalLabel: 'entries',
    prev: 'Previous',
    next: 'Next',
    emptyTitle: 'No activity',
    emptyDesc: 'Changes to the workspace will appear here.',
    emptyFilteredTitle: 'No results',
    emptyFilteredDesc: 'No entry matches the active filters.',
    // Roles and permissions matrix
    matrixTitle: 'Roles and permissions',
    matrixSubtitle: 'What each system role can do in this workspace.',
    matrixActionCol: 'Action',
    matrixEmptyTitle: 'No roles',
    matrixEmptyDesc: 'No roles are defined in this workspace.',
    matrixNoAccess: 'No access',
    matrixGranted: (scope: string): string => `Allowed at ${scope} scope`,
    // Security and compliance posture
    complianceTitle: 'Security and compliance',
    complianceSubtitle: 'The platform security and compliance posture for EU customers.',
    items: {
      gdpr: {
        title: 'GDPR',
        desc: 'Data processing compliant with EU Regulation 2016/679.',
        status: 'Compliant',
      },
      aiAct: {
        title: 'EU AI Act',
        desc: 'Aligned with the European artificial intelligence regulation.',
        status: 'Aligned',
      },
      encryption: {
        title: 'Encryption',
        desc: 'Data encrypted in transit and at rest.',
        status: 'Active',
      },
      noTraining: {
        title: 'No training',
        desc: 'Your data never trains third-party AI models.',
        status: 'Guaranteed',
      },
      residency: {
        title: 'Data residency',
        desc: 'Data stored and processed within the European Union.',
        status: 'Active',
      },
      isolation: {
        title: 'Multi-tenant isolation',
        desc: 'Every workspace is isolated at the application and database layer.',
        status: 'Active',
      },
    },
  },
  // ── Automazioni (workflow list + curated template library) ─────────────────
  automazioni: {
    // Tabs
    tabMine: 'My workflows',
    tabLibrary: 'Library',
    tablistLabel: 'Automations sections',
    // Workflow list
    listHeading: 'Available workflows',
    emptyTitle: 'No workflows',
    emptyDesc: 'Create your first workflow to automate repetitive operations.',
    // Detail
    detailHeading: 'Details',
    detailPlaceholder: 'Select a workflow to see its details and run it.',
    stepsHeading: 'Workflow steps',
    stepsEmpty: 'No steps defined.',
    stepFallback: (n: number): string => `Step ${n}`,
    approvalBadge: 'Approval',
    approvalTitle: 'Requires manual approval',
    // Step type labels (for template-instantiated steps that carry only a type)
    stepTypeAiAction: 'AI action',
    stepTypeRequireApproval: 'Manual approval',
    stepTypeSendNotification: 'Notification',
    stepTypeCallIntegration: 'Integration',
    stepTypeCreateTask: 'Create task',
    stepTypeCondition: 'Condition',
    stepTypeDelay: 'Delay',
    // Run action
    run: 'Run',
    running: 'Starting...',
    runSuccess: 'Workflow started successfully.',
    runError: 'Could not start the workflow. Try again.',
    latestRunHeading: 'Latest started run',
    // Run card
    currentStep: (step: string): string => `Current step: ${step}`,
    startedAt: (rel: string): string => `Started ${rel}`,
    completedAt: (dt: string): string => `Completed ${dt}`,
    hideLog: 'Hide log',
    showLog: (n: number): string => `Show log (${n})`,
    // Recent runs
    runsThisWorkflow: 'Runs of this workflow',
    runsRecent: 'Recent runs',
    runsEmptyTitle: 'No runs',
    runsEmptyDesc: 'Workflow runs show up here with their status and log.',
    // Step count
    stepCount: (n: number): string => `${n} step${n === 1 ? '' : 's'}`,
    // Workflow status labels
    statusActive: 'Active',
    statusInactive: 'Inactive',
    statusDraft: 'Draft',
    statusPaused: 'Paused',
    statusRunning: 'Running',
    statusNeedsContext: 'Context needed',
    statusNeedsApproval: 'Approval needed',
    statusCompleted: 'Completed',
    statusFailed: 'Failed',
    statusCancelled: 'Cancelled',
    statusError: 'Error',
    // Run status labels
    runStatusPending: 'Pending',
    runStatusRunning: 'Running',
    runStatusCompleted: 'Completed',
    runStatusFailed: 'Failed',
    runStatusCancelled: 'Cancelled',
    // Library tab
    libraryHeading: 'Workflow library',
    libraryDesc:
      'Ready-to-use templates. Add a template to create a draft workflow in your workspace.',
    libraryEmptyTitle: 'No templates',
    libraryEmptyDesc: 'Workflow templates will show up here.',
    use: 'Use',
    using: 'Creating...',
    useSuccess: (name: string): string => `Draft created: ${name}. Find it under your workflows.`,
    useError: 'Could not create the workflow from the template. Try again.',
    // Template category labels
    categoryProductivity: 'Productivity',
    categoryFinance: 'Finance',
    categorySales: 'Sales',
    categoryOperations: 'Operations',
  },
  integrazioni: {
    title: 'Integrations',
    subtitle: 'Connect and manage your data sources and the AI connector.',
    loadError: 'Could not load integrations. Try again.',
    connectedBanner: (provider: string): string => `${provider} connected successfully.`,
    connectFailedBanner: (provider: string): string => `Connecting ${provider} failed. Try again.`,
    disconnectAction: 'Disconnect',
    disconnecting: 'Disconnecting...',
    disconnectError: 'Could not disconnect. Try again.',
    sectionConnectors: 'Data sources',
    sectionConnectorsDesc: 'Every connected source feeds the workspace knowledge base.',
    mcpTitle: 'Claude connector (MCP)',
    mcpDesc:
      'Generate a connector link and add it in Claude (Settings > Connectors > Add custom connector) to query this workspace.',
    mcpNameLabel: 'Token name',
    mcpNamePlaceholder: "e.g. Marco's Claude",
    mcpCreate: 'Generate connector',
    mcpCreating: 'Generating...',
    mcpCreateError: 'Could not generate the token. Try again.',
    mcpUrlLabel: 'Connector URL (shown only now)',
    mcpUrlWarning:
      'Copy it now: it contains the secret key and will not be shown again. Treat it like a password.',
    mcpCopy: 'Copy URL',
    mcpCopied: 'URL copied',
    mcpCopyError: 'Copy failed. Select and copy manually.',
    mcpListTitle: 'Active tokens',
    mcpListEmpty: 'No tokens yet.',
    mcpRevoke: 'Revoke',
    mcpRevoking: 'Revoking...',
    mcpRevoked: 'Revoked',
    mcpRevokeError: 'Could not revoke. Try again.',
    mcpLastUsed: (when: string): string => `Last used: ${when}`,
    mcpNeverUsed: 'Never used',
  },
  abbonamento: {
    currentPlanTitle: 'Your plan',
    currentPlanNone: 'Trial period',
    statusLabel: 'Status',
    renewalLabel: 'Next renewal',
    trialLabel: 'Trial until',
    cancelAtPeriodEnd: 'The plan ends at the next renewal.',
    managedHint: 'Managed through Stripe.',
    statusTrial: 'Trial',
    statusActive: 'Active',
    statusPastDue: 'Payment overdue',
    statusCancelled: 'Cancelled',
    statusUnknown: 'Unknown',
    choosePlanTitle: 'Choose a plan',
    choosePlanDesc: 'Move up to a higher plan whenever you want. No commitment.',
    intervalMonthly: 'Monthly',
    intervalYearly: 'Yearly',
    intervalYearlyHint: '2 months free',
    currentBadge: 'Current plan',
    upgradeAction: 'Upgrade plan',
    starting: 'Opening...',
    manageTitle: 'Subscription management',
    manageDesc: 'Update your payment method, download invoices, or cancel.',
    manageAction: 'Manage subscription',
    opening: 'Opening...',
    planStarterName: 'Starter',
    planStarterDesc: 'For getting started: clients, projects, and invoices in one place.',
    planProfessionalName: 'Professional',
    planProfessionalDesc: 'AI automations, reconciliations, and a multichannel inbox.',
    planBusinessName: 'Business',
    planBusinessDesc: 'Team, advanced roles, and industry workflows.',
    paymentsNotConfigured: 'Payments are not active yet.',
    checkoutError: 'Could not start the payment. Try again.',
    portalError: 'Could not open management. Try again.',
    loadError: 'Could not load the subscription status. Try again.',
    returnOk: 'Payment completed. We are updating your plan shortly.',
    returnCancelled: 'Payment cancelled. No charge was made.',
  },
  consent: {
    labelPrefix: 'I accept the ',
    termsLink: 'Terms',
    and: ' and the ',
    privacyLink: 'Privacy Policy',
    required: 'To continue you must accept the Terms and the Privacy Policy.',
  },
};
